import { useState, useEffect } from "react"
import { useUpdateComplainStatus } from "@/hooks/complain/useUpdateComplainStatus"
import { useGetMyComplains } from "@/hooks/complain/useGetMyComplains"
import { useGetAllComplains } from "@/hooks/complain/useGetAllComplains"
import { useCreateComplain } from "@/hooks/complain/useCreateComplain"
import { useGetComplainById } from "@/hooks/complain/useGetComplainById"
import { useAuthStore } from "@/store/auth.store"
import { cn } from "@/lib/utils"
import {
  AlertTriangle,
  Loader2,
  Plus,
  ChevronRight,
  ChevronLeft,
  Calendar,
  FileText,
  MessageSquare,
  Globe,
  Package,
  CreditCard,
  Truck,
  HelpCircle,
  X,
  MoreVertical,
} from "lucide-react"

function formatDate(isoStr: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

// ── Status & Category design tokens ─────────────────────────────────────────
// Kept in one place so badge colors stay consistent between the list card
// and the detail view instead of being redefined per-usage.

const STATUS_STYLES: Record<string, string> = {
  OPEN: "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900/50",
  IN_PROGRESS: "bg-sky-50 dark:bg-sky-950/30 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-900/50",
  RESOLVED: "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/50",
  CLOSED: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700",
  REJECTED: "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900/50",
}

function getStatusColor(status?: string) {
  return STATUS_STYLES[status || "OPEN"] || STATUS_STYLES.OPEN
}

const CATEGORY_META: Record<string, { label: string; icon: any; className: string }> = {
  PRODUCT_QUALITY: { label: "Product Quality", icon: Package, className: "bg-rose-50 dark:bg-rose-950/20 text-[var(--primary)] border-rose-100 dark:border-rose-900/30" },
  PAYMENT: { label: "Payment", icon: CreditCard, className: "bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900/30" },
  LOGISTICS: { label: "Logistics", icon: Truck, className: "bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400 border-amber-100 dark:border-amber-900/30" },
  COMMUNICATION: { label: "Communication", icon: MessageSquare, className: "bg-violet-50 dark:bg-violet-950/20 text-violet-700 dark:text-violet-400 border-violet-100 dark:border-violet-900/30" },
  OTHER: { label: "Other", icon: HelpCircle, className: "bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800" },
}

function getCategoryMeta(category: string) {
  return CATEGORY_META[category] || { label: category?.replace(/_/g, " ") || "Other", icon: HelpCircle, className: "bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800" }
}

function StatusBadge({ status }: { status?: string }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold uppercase tracking-wide",
        getStatusColor(status)
      )}
    >
      <span className="size-1.5 rounded-full bg-current" />
      {(status || "OPEN").replace(/_/g, " ")}
    </span>
  )
}

function CategoryBadge({ category }: { category: string }) {
  const meta = getCategoryMeta(category)
  const Icon = meta.icon
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold uppercase tracking-wide", meta.className)}>
      <Icon className="size-3" />
      {meta.label}
    </span>
  )
}

// ── Complaint List Card ──────────────────────────────────────────────────────

function ComplaintCard({ complain, currentUser, onSelect, hideUser = false }: { complain: any; currentUser: UserInfo | null; onSelect: (id: string) => void; hideUser?: boolean }) {
  const displayUser = complain.user || currentUser

  return (
    <div
      onClick={() => onSelect(complain.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onSelect(complain.id)
        }
      }}
      aria-label={`Complaint ${complain.id}, ${getCategoryMeta(complain.category).label}, status ${complain.status || "OPEN"}`}
      className="group cursor-pointer rounded-[var(--radius-lg)] border-[0.5px] border-[var(--outline-variant)] bg-white dark:bg-slate-950 p-4 transition-colors hover:border-[var(--primary)] text-left font-sans"
    >
      {/* Header: category + status */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CategoryBadge category={complain.category} />
        <StatusBadge status={complain.status} />
      </div>

      {/* Message preview */}
      <p className="mt-3 text-xs text-slate-700 dark:text-slate-300 font-medium leading-relaxed line-clamp-2" title={complain.message}>
        {complain.message}
      </p>

      {/* Footer */}
      <div className="mt-3.5 flex items-center justify-between gap-3 border-t border-[var(--outline-variant)]/40 pt-3">
        <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500">
          {displayUser && !hideUser && (
            <>
              <span className="truncate font-semibold text-slate-700 dark:text-slate-300">{displayUser.name}</span>
              {displayUser.role && (
                <span className="shrink-0 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-400">
                  {displayUser.role}
                </span>
              )}
              <span className="shrink-0 text-slate-300 dark:text-slate-700">·</span>
            </>
          )}
          <span className="shrink-0 flex items-center gap-1">
            <Calendar className="size-3" />
            {new Date(complain.createdAt).toLocaleDateString()}
          </span>
        </div>
        <span className="shrink-0 flex items-center gap-0.5 text-xs font-bold text-[var(--primary)] group-hover:text-[var(--primary-container)] group-hover:underline transition-colors">
          Details <ChevronRight className="size-3.5" />
        </span>
      </div>
    </div>
  )
}

function ComplaintCardSkeleton() {
  return (
    <div className="rounded-[var(--radius-lg)] border-[0.5px] border-[var(--outline-variant)] bg-white dark:bg-slate-950 p-4 animate-pulse">
      <div className="flex justify-between gap-2">
        <div className="h-5 w-28 rounded-full bg-slate-100 dark:bg-slate-800" />
        <div className="h-5 w-20 rounded-full bg-slate-100 dark:bg-slate-800" />
      </div>
      <div className="mt-4 h-3 w-full rounded bg-slate-100 dark:bg-slate-800" />
      <div className="mt-2 h-3 w-2/3 rounded bg-slate-100 dark:bg-slate-800" />
      <div className="mt-4 flex justify-between border-t border-[var(--outline-variant)]/40 pt-3">
        <div className="h-3 w-24 rounded bg-slate-100 dark:bg-slate-800" />
        <div className="h-3 w-16 rounded bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
  )
}

// ── Complain Detail View Component ───────────────────────────────────────────
interface UserInfo {
  name: string
  email: string
  role?: string
}

export function ComplainDetailView({ id, onBack, currentUser, isAdmin, hideUser = false }: { id: string; onBack: () => void; currentUser: UserInfo | null; isAdmin: boolean; hideUser?: boolean }) {
  const { data: complain, isLoading, error } = useGetComplainById(id)
  const displayUser = complain?.user || currentUser

  const updateMutation = useUpdateComplainStatus()
  const [editStatus, setEditStatus] = useState<string>("OPEN")
  const [resolutionNote, setResolutionNote] = useState<string>("")
  const [isEditingAdmin, setIsEditingAdmin] = useState(false)

  useEffect(() => {
    if (complain) {
      setEditStatus(complain.status || "OPEN")
      setResolutionNote(complain.resolutionNote || "")
    }
  }, [complain])

  return (
    <div className="animate-in fade-in duration-200 text-left font-sans max-w-5xl mx-auto space-y-4">
      {/* Header / Navigation Row */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer p-1"
          title="Back to Complaints"
          aria-label="Back to Complaints list"
        >
          <ChevronLeft className="size-4" />
        </button>
          <span className="text-sm text-slate-700 font-medium ">Back to Complaints</span>

      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white dark:bg-slate-950 border border-[var(--outline-variant)] rounded-[var(--radius-lg)]">
          <Loader2 className="size-8 animate-spin text-[var(--primary)]" />
          <p className="text-xs font-medium text-slate-500">Loading complaint details...</p>
        </div>
      ) : error || !complain ? (
        <div className="bg-white dark:bg-slate-950 border border-[var(--outline-variant)] rounded-[var(--radius-lg)] p-10 text-center text-slate-400">
          <AlertTriangle className="size-10 mx-auto text-slate-300 mb-3" />
          <p className="text-xs font-bold text-slate-600">Error Loading Details</p>
          <p className="text-xs text-slate-400 mt-1">
            Could not fetch information for this complaint.
          </p>
        </div>
      ) : (
        <div className="group relative overflow-hidden bg-white dark:bg-slate-950 border border-[var(--outline-variant)] rounded-[var(--radius-lg)] p-5 space-y-4">
          {/* Header row: category + status badges, consistent with list card */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <CategoryBadge category={complain.category} />
              <StatusBadge status={complain.status} />
            </div>
            <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
              <Calendar className="size-3.5 text-slate-400" />
              Filed {formatDate(complain.createdAt)}
            </p>
          </div>

          <div className="h-px bg-slate-100 dark:bg-slate-800 w-full" />

          {/* Meta grid: submitted by / role — spec-row style, consistent scale */}
          {displayUser && !hideUser && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50/50 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-800 rounded-[var(--radius-md)] p-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Submitted By</p>
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-0.5">{displayUser.name}</p>
              </div>
              {displayUser.role && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">User Role</p>
                  <span className="inline-block mt-1 text-slate-600 dark:text-slate-400 text-xs font-bold ">
                    {displayUser.role}
                  </span>
                </div>
              )}
            </div>
          )}

          <div>
            <span className="text-xs text-slate-400 font-semibold uppercase tracking-wide flex items-center gap-1 mb-2">
              <MessageSquare className="size-3.5 text-[var(--primary)]" />Message
            </span>
            <div className="bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[var(--radius-md)] p-4">
              <p className="text-xs text-slate-600 dark:text-slate-300 font-medium leading-relaxed whitespace-pre-wrap">
                {complain.message}
              </p>
            </div>
          </div>

          {complain.documentUrls && complain.documentUrls.length > 0 && (
            <div>
              <span className="text-xs text-slate-400 font-semibold uppercase tracking-wide flex items-center gap-1 mb-2">
                <FileText className="size-3.5 text-[var(--primary)]" /> Documents & Attachments ({complain.documentUrls.length})
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {complain.documentUrls.map((url: string, index: number) => (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={url}
                    className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 hover:bg-[var(--surface)] dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 hover:text-[var(--primary)] border border-slate-100 dark:border-slate-800 hover:border-[var(--outline-variant)] rounded-[var(--radius-md)] p-3 transition-colors text-xs font-medium"
                  >
                    <Globe className="size-4 shrink-0 text-slate-400" />
                    <span className="truncate flex-1 text-left">{url}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Show Resolution Note if it exists */}
          {complain.resolutionNote && (
            <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-5">
               <span className="text-xs text-slate-400 font-semibold uppercase tracking-wide flex items-center gap-1 mb-2">
                <MessageSquare className="size-3.5 text-[var(--primary)]" /> Resolution Note
              </span>
              <div className="bg-[var(--surface)]/50 dark:bg-slate-900 border border-[var(--outline-variant)]/40 rounded-[var(--radius-md)] p-4">
                <p className="text-xs text-[var(--primary)] dark:text-red-400 font-medium leading-relaxed whitespace-pre-wrap">
                  {complain.resolutionNote}
                </p>
              </div>
            </div>
          )}

          {/* Admin Update Section */}
          {isAdmin && complain && (
            <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                  <AlertTriangle className="size-3.5 text-[var(--primary)]" /> Admin Controls
                </h4>
                {!isEditingAdmin && (
                  <button
                    onClick={() => setIsEditingAdmin(true)}
                    className="text-xs font-bold uppercase tracking-wide text-[var(--primary)] bg-[var(--surface)] hover:bg-[var(--primary)]/10 border border-[var(--outline-variant)]/40 px-3 py-1.5 rounded-[var(--radius-md)] transition-colors cursor-pointer"
                  >
                    Update Complaint
                  </button>
                )}
              </div>

              {isEditingAdmin && (
                <div className="bg-slate-50 dark:bg-slate-900 border border-[var(--outline-variant)]/30 rounded-[var(--radius-md)] p-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label htmlFor="admin-status-select" className="text-xs font-semibold uppercase tracking-wide text-slate-500 block">
                        Update Status
                      </label>
                      <div className="relative">
                        <select
                          id="admin-status-select"
                          value={editStatus}
                          onChange={(e) => setEditStatus(e.target.value)}
                          className="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-[var(--radius-md)] pl-3 pr-8 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 focus:outline-none focus:border-[var(--primary)] transition-colors cursor-pointer appearance-none"
                        >
                          <option value="OPEN">Open</option>
                          <option value="IN_PROGRESS">In Progress</option>
                          <option value="RESOLVED">Resolved</option>
                          <option value="CLOSED">Closed</option>
                          <option value="REJECTED">Rejected</option>
                        </select>
                        <ChevronRight className="size-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 rotate-90 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="admin-resolution-textarea" className="text-xs font-semibold uppercase tracking-wide text-slate-500 block">
                      Resolution Note
                    </label>
                    <textarea
                      id="admin-resolution-textarea"
                      placeholder="Provide context on resolution or rejection..."
                      value={resolutionNote}
                      onChange={(e) => setResolutionNote(e.target.value)}
                      className="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-[var(--radius-md)] p-3 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-[var(--primary)] transition-colors resize-y min-h-[60px]"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingAdmin(false)
                        setEditStatus(complain.status || "OPEN")
                        setResolutionNote(complain.resolutionNote || "")
                      }}
                      className="text-xs font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 px-4 py-2 rounded-[var(--radius-md)] transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={updateMutation.isPending || (editStatus === complain.status && resolutionNote === (complain.resolutionNote || ""))}
                      onClick={() => {
                        updateMutation.mutate({
                          id: complain.id,
                          status: editStatus,
                          resolutionNote: resolutionNote.trim() || undefined
                        }, {
                          onSuccess: () => setIsEditingAdmin(false)
                        })
                      }}
                      className="text-xs font-bold uppercase tracking-wide text-white bg-[var(--primary)] hover:bg-[var(--primary-container)] px-5 py-2 rounded-[var(--radius-md)] transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-2"
                    >
                      {updateMutation.isPending ? (
                        <>
                          <Loader2 className="size-3 animate-spin" /> Updating...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Complaints Page ─────────────────────────────────────────────────────
export function ComplaintsPage({ role, onSelectComplaint }: { role: "buyer" | "seller" | "admin"; onSelectComplaint?: (id: string) => void }) {
  const isUser = role !== "admin"
  const currentUser = useAuthStore((s) => s.user)

  // Fetching data based on role
  const { data: userComplaints = [], isLoading: isUserLoading } = useGetMyComplains(isUser)
  const { data: adminComplaints = [], isLoading: isAdminLoading } = useGetAllComplains(!isUser)

  const complaints = isUser ? userComplaints : adminComplaints
  const isLoading = isUser ? isUserLoading : isAdminLoading

  const createMutation = useCreateComplain()

  // Local state
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [showActionsDropdown, setShowActionsDropdown] = useState(false)
  const [category, setCategory] = useState<"PRODUCT_QUALITY" | "PAYMENT" | "LOGISTICS" | "COMMUNICATION" | "OTHER">("PRODUCT_QUALITY")
  const [message, setMessage] = useState("")
  // Multiple attachments — the API already accepts documentUrls: string[],
  // this just exposes that instead of capping the form at one link.
  const [docUrls, setDocUrls] = useState<string[]>([""])

  const handleAddDocUrl = () => setDocUrls([...docUrls, ""])
  const handleRemoveDocUrl = (index: number) => setDocUrls(docUrls.filter((_, i) => i !== index))
  const handleDocUrlChange = (index: number, value: string) => {
    const updated = [...docUrls]
    updated[index] = value
    setDocUrls(updated)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    createMutation.mutate(
      {
        category,
        message,
        documentUrls: docUrls.map((u) => u.trim()).filter(Boolean),
      },
      {
        onSuccess: () => {
          setMessage("")
          setDocUrls([""])
          setShowAddForm(false)
        },
      }
    )
  }

  // Render detail view if a complaint is selected
  if (selectedId && !onSelectComplaint) {
    return <ComplainDetailView id={selectedId} onBack={() => setSelectedId(null)} currentUser={currentUser} isAdmin={!isUser} />
  }

  return (
    <div className="space-y-5 text-left font-sans animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="hidden sm:inline text-xl font-black text-[#291716] dark:text-slate-100 tracking-tight flex items-center gap-2">
            Complaints Panel
          </h2>
          <p className="text-sm text-slate-800 mt-0.5">
            {role === "admin"
              ? "Review and manage all submitted complaints across the platform."
              : "Submit and track complaints regarding products, payments, or logistics."}
          </p>
        </div>

        {isUser && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowActionsDropdown(!showActionsDropdown)}
              className="p-1.5 text-slate-500 hover:text-[#be001e] hover:bg-[#be001e]/5 hover:border-[#be001e]/30 rounded-lg transition-all border border-slate-200 cursor-pointer flex items-center justify-center"
              title="Actions"
            >
              <MoreVertical className="size-4" />
            </button>
            {showActionsDropdown && (
              <>
                <div 
                  className="fixed inset-0 z-30" 
                  onClick={() => setShowActionsDropdown(false)} 
                />
                <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setShowActionsDropdown(false)
                      setShowAddForm(!showAddForm)
                    }}
                    className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-1.5 cursor-pointer"
                  >
                    <Plus className="size-4 text-slate-500" />
                    File Complaint
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {showAddForm && isUser && (
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-slate-950 border border-[var(--outline-variant)]/30 rounded-[var(--radius-lg)] p-5 space-y-4 animate-in slide-in-from-top duration-200"
        >
          <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200">
            File a new complaint
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label htmlFor="form-category-select" className="text-xs font-semibold uppercase tracking-wide text-slate-500 block">
                Category
              </label>
              <div className="relative">
                <select
                  id="form-category-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as any)}
                  className="w-full h-9 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[var(--radius-md)] pl-3 pr-8 py-2 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-[var(--primary)] transition-colors cursor-pointer appearance-none"
                >
                  <option value="PRODUCT_QUALITY">Product Quality</option>
                  <option value="PAYMENT">Payment</option>
                  <option value="LOGISTICS">Logistics</option>
                  <option value="COMMUNICATION">Communication</option>
                  <option value="OTHER">Other</option>
                </select>
                <ChevronRight className="size-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 rotate-90 pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="form-message-textarea" className="text-xs font-semibold uppercase tracking-wide text-slate-500 block">
              Message
            </label>
            <textarea
              id="form-message-textarea"
              required
              rows={4}
              placeholder="Detail your issue here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[var(--radius-md)] px-3 py-2 text-xs text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:border-[var(--primary)] transition-colors resize-none"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Documents / Links (Optional)
              </span>
              <button
                type="button"
                onClick={handleAddDocUrl}
                className="flex items-center gap-1 text-xs font-bold text-[var(--primary)] hover:text-[var(--primary-container)] transition-colors cursor-pointer"
              >
                <Plus className="size-3" /> Add another
              </button>
            </div>
            {docUrls.map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="url"
                  id={`form-doc-url-${index}`}
                  aria-label={`Document attachment link ${index + 1}`}
                  placeholder="https://example.com/receipt.pdf"
                  value={url}
                  onChange={(e) => handleDocUrlChange(index, e.target.value)}
                  className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[var(--radius-md)] px-3 py-2 text-xs text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:border-[var(--primary)] transition-colors"
                />
                {docUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveDocUrl(index)}
                    className="flex items-center justify-center h-11 w-11 rounded-[var(--radius-md)] border border-red-100 dark:border-red-950 bg-red-50 dark:bg-red-950/20 text-[var(--primary-container)] hover:bg-red-100 transition-colors cursor-pointer shrink-0"
                    title="Remove attachment field"
                    aria-label={`Remove document attachment link ${index + 1}`}
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="text-xs font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 px-4 py-2 rounded-[var(--radius-md)] transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="text-xs font-bold text-white bg-[var(--primary)] hover:bg-[var(--primary-container)] px-4 py-2 rounded-[var(--radius-md)] transition-colors disabled:opacity-50 cursor-pointer"
            >
              {createMutation.isPending ? "Submitting..." : "Submit Complaint"}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <ComplaintCardSkeleton key={i} />
          ))}
        </div>
      ) : complaints.length === 0 ? (
        <div className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-800 rounded-[var(--radius-lg)] p-10 text-center text-slate-400">
          <AlertTriangle className="size-10 mx-auto text-slate-300 mb-3" />
          <p className="text-xs font-bold text-slate-600 dark:text-slate-300">No Complaints Recorded</p>
          <p className="text-xs text-slate-400 mt-1">
            Any filed complaints will be logged and displayed in this list.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {complaints.map((c) => (
            <ComplaintCard
              key={c.id}
              complain={c}
              currentUser={currentUser}
              hideUser={role === "buyer" || role === "seller"}
              onSelect={(id) => {
                if (onSelectComplaint) {
                  onSelectComplaint(id)
                } else {
                  setSelectedId(id)
                  setShowAddForm(false)
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}